import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { LocationsApi } from '@frontend/api-locations';
import { QuickFillApi } from '@frontend/api-quick-fill';
import { ScheduleQueries } from '@frontend/api-schedule';
import { useTranslation } from '@frontend/i18n';
import { useQuery } from '@frontend/react-query-helpers';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { MessageTemplate, Stepper, Text, useAlert } from '@frontend/design-system';
import { quickfillSendMessageTrackingIds } from '../../tracking-ids';
import { SpinnerLoader } from '../components/spinning-loader';
import { useQuickFillTrayContext } from '../context';
import { useQuickFillRecipientShallowStore } from '../store';
import { defaultTemplate, getTemplateTags, getTimeIn12HrFormat } from './utils';

type Props = {
  locationId: string;
  date: string;
  timeRange: string[];
};

export const ComposeMessage = ({ locationId, date, timeRange }: Props) => {
  const alert = useAlert();
  const { t } = useTranslation('schedule-quick-fill');
  const { closeModal } = useQuickFillTrayContext();

  const [template, setTemplate] = useState<string>(defaultTemplate);

  const { selectedIds } = useQuickFillRecipientShallowStore('selectedIds');

  const { data: locationData, isLoading: isLoadingLocationData } = useQuery({
    queryKey: ['quick-fill', 'locations', locationId],
    queryFn: () => LocationsApi.getLocation(locationId),
    enabled: !!locationId,
    onError: () => alert.error(t('Failed to get location data for Quick Fill')),
  });

  const { mutateAsync: sendMessage, isLoading: isSending } = ScheduleQueries.useSendQuickFillMessage();

  const {
    data: manualTemplateData,
    isLoading: isLoadingManualTemplateData,
    refetch: refetchManualTemplates,
  } = useQuery({
    queryKey: ['scheduleQuickFillListTemplate', locationId],
    queryFn: () => QuickFillApi.getDefaultTemplate(locationId),
    enabled: !!locationId,
    onError: () => alert.error(t('Failed to get custom message template')),
  });

  const formattedDateTimeObj = useMemo(() => {
    const formattedDate = dayjs(date).format('dddd MMMM D YYYY');
    const formattedTimeRange = `${getTimeIn12HrFormat(formattedDate, timeRange[0])} - ${getTimeIn12HrFormat(
      formattedDate,
      timeRange[1]
    )}`;
    const formattedDateAndTime = `${formattedDate}, ${formattedTimeRange}`;

    return {
      date: formattedDate,
      timeRange: formattedTimeRange,
      dateAndTime: formattedDateAndTime,
    };
  }, [date, timeRange]);

  const tags = useMemo(
    () => (locationData ? getTemplateTags(locationData, formattedDateTimeObj.dateAndTime) : []),
    [locationData, formattedDateTimeObj.dateAndTime]
  );

  const sendQuickFillMessage = async () => {
    const startTime = dayjs(`${formattedDateTimeObj.date} ${timeRange[0]}`).toISOString();
    const endTime = dayjs(`${formattedDateTimeObj.date} ${timeRange[1]}`).toISOString();

    sendMessage({
      locationId,
      personIds: selectedIds,
      startTime,
      endTime,
    })
      .then(() => {
        alert.success(t('Quick Fill message sent successfully'));
        closeModal();
      })
      .catch(() => {
        alert.error(t('Failed to send Quick Fill message'));
      });
  };

  const handleSubmit = async () => {
    if (!(selectedIds.length && locationId)) return;

    const initialTemplate = manualTemplateData?.Template || defaultTemplate;
    const isUpdateTemplate = template !== initialTemplate;

    const updateTemplatePromise = isUpdateTemplate ? QuickFillApi.updateTemplate(template) : Promise.resolve();
    const sendMessagePromise = sendQuickFillMessage();

    try {
      await Promise.all([updateTemplatePromise, sendMessagePromise]);

      if (isUpdateTemplate) {
        refetchManualTemplates();
      }
    } catch (error) {
      sentry.error({ topic: 'scheduling', error });
    }
  };

  useEffect(() => {
    if (manualTemplateData?.Template) {
      setTemplate(manualTemplateData?.Template);
    }
  }, [manualTemplateData?.Template]);

  if (isLoadingLocationData || isLoadingManualTemplateData) return <SpinnerLoader />;

  return (
    <>
      <MessageTemplate
        draggableTags
        tags={tags}
        initialTemplate={manualTemplateData?.Template || template}
        onChange={(template) => setTemplate(template)}
      >
        <div css={containerStyle}>
          <MessageTemplate.Editor />
          <Text size='small' color='subdued'>
            {t('You can also drag and drop, or click a tag below, to add your message')}
          </Text>
          <MessageTemplate.TagList />
        </div>
      </MessageTemplate>
      <Stepper.ButtonBar helperText={template.length === 0 ? t('Compose a message') : ''}>
        <Stepper.NextButton
          trackingId={quickfillSendMessageTrackingIds.composeMessageNextButton}
          isValid={template.length > 0 && !isSending}
          onSubmit={handleSubmit}
        >
          {t('Send')}
        </Stepper.NextButton>
      </Stepper.ButtonBar>
    </>
  );
};

const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
`;
